import { Alert, Box, FormGroup, FormSection } from '@ksyos/design-system';
import type { IService } from '@common/interfaces/';
import type { ValidationErrors } from '@frontend/utils/';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/';
import { CheckedEmail } from '../../../organizations/emailNotificationSettings/CheckedEmail';
import { SettingsCheckboxInput } from '../../../organizations/organizationAddCarePath/components/SettingCheckboxInput';
import { SettingsSwitch } from '../../modals/components/SettingsSwitch';

export interface IEmailNotifications {
    emailAddressOrganizationInbox: string | null;
    emailAddressOrganizationInboxAlt: string | null;
    sendNotificationOrganizationEmail: boolean | null;
    sendNotificationOrganizationInbox: boolean;
}

export interface IEmailNotificationsProps {
    emailNotifications: IEmailNotifications;
    errorPrefix?: string;
    errors?: ValidationErrors;
    onChange: (key: keyof IEmailNotifications, value: IEmailNotifications[keyof IEmailNotifications]) => void;
    onDoValidate?: (key: keyof IEmailNotifications) => void;
    organizationEmailAddressRequestInbox?: string;
    service: IService;
}

export function EmailNotifications(props: IEmailNotificationsProps) {
    return (
        <FormSection title={translateKey('serviceOrganizationSetting.emailNotifications')}>
            <FormGroup
                hasErrors={hasError('sendNotificationOrganizationInbox')}
                label={translateKey('serviceOrganizationSetting.emailNotificationOnlyToOrganization')}
            >
                <SettingsSwitch
                    error={getErrorMessage('sendNotificationOrganizationInbox')}
                    listKey={`sendNotificationOrganizationInbox-${props.service.id}`}
                    onBlur={() => handleValidateOne('sendNotificationOrganizationInbox')}
                    onChange={(value) => props.onChange('sendNotificationOrganizationInbox', value)}
                    value={props.emailNotifications.sendNotificationOrganizationInbox}
                />
            </FormGroup>

            {props.emailNotifications.sendNotificationOrganizationInbox && (
                <FormSection isBorderless>
                    <FormGroup
                        hasErrors={
                            hasError('sendNotificationOrganizationEmail') ||
                            hasError('emailAddressOrganizationInbox') ||
                            hasError('emailAddressOrganizationInboxAlt')
                        }
                        label={translateKey('personalSettings.notifications.receiveOn')}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            {props.organizationEmailAddressRequestInbox && (
                                <CheckedEmail
                                    email={props.organizationEmailAddressRequestInbox}
                                    error={getErrorMessage('sendNotificationOrganizationEmail')}
                                    isChecked={Boolean(props.emailNotifications.sendNotificationOrganizationEmail)}
                                    onBlur={async () => handleValidateOne('sendNotificationOrganizationEmail')}
                                    onChange={async (isChecked) =>
                                        props.onChange('sendNotificationOrganizationEmail', isChecked)
                                    }
                                    readOnly
                                />
                            )}

                            <SettingsCheckboxInput
                                error={getErrorMessage('emailAddressOrganizationInbox')}
                                onChange={(value) => props.onChange('emailAddressOrganizationInbox', value)}
                                onDoValidate={() => handleValidateOne('emailAddressOrganizationInbox')}
                                placeholder={translateKey('common.email')}
                                text={props.emailNotifications.emailAddressOrganizationInbox}
                            />

                            <SettingsCheckboxInput
                                error={getErrorMessage('emailAddressOrganizationInboxAlt')}
                                onChange={(value) => props.onChange('emailAddressOrganizationInboxAlt', value)}
                                onDoValidate={() => handleValidateOne('emailAddressOrganizationInboxAlt')}
                                placeholder={translateKey('common.email')}
                                text={props.emailNotifications.emailAddressOrganizationInboxAlt}
                            />
                        </Box>
                    </FormGroup>
                </FormSection>
            )}
            {props.emailNotifications.sendNotificationOrganizationInbox &&
                props.errors?.valueSeq().includes('email_required') && (
                    <Alert appearance="warning">{translateKey('carePath.noEmailSelected')}</Alert>
                )}
        </FormSection>
    );

    function hasError(key: keyof IEmailNotifications) {
        const errorKey = getErrorKey(key);

        return props.errors?.has(errorKey);
    }

    function getErrorMessage(key: keyof IEmailNotifications) {
        const errorKey = getErrorKey(key);

        return getValidationErrorTranslation(props.errors?.get(errorKey));
    }

    function getErrorKey(key: keyof IEmailNotifications) {
        return `${props.errorPrefix ?? ''}${key}`;
    }

    function handleValidateOne(key: keyof IEmailNotifications) {
        if (props.onDoValidate) {
            props.onDoValidate(key);
        }
    }
}
